import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';

/**
 * Relationship types for account links
 */
export const RELATIONSHIP_TYPES = {
  MANAGER_REPORT: 'manager-report',  // One person manages the other
  PEER: 'peer'                        // Colleagues at same level
};

/**
 * Hook for managing account links between users
 * Unified system for linking accounts with relationship types and sharing controls
 * Replaces both Team Hierarchy and separate Account Linking features
 */
export function useAccountLinks() {
  const { user, userProfile } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResult, setSearchResult] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Subscribe to account links using two queries (userA and userB)
  // This matches iOS behavior and security rules that check userA/userB
  useEffect(() => {
    if (!user?.uid) {
      setLinks([]);
      setLoading(false);
      return;
    }

    let linksAsA = [];
    let linksAsB = [];

    // Query where user is userA
    const queryA = query(
      collection(db, 'accountLinks'),
      where('userA', '==', user.uid)
    );

    // Query where user is userB
    const queryB = query(
      collection(db, 'accountLinks'),
      where('userB', '==', user.uid)
    );

    const unsubscribeA = onSnapshot(
      queryA,
      (snapshot) => {
        linksAsA = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        // Merge and dedupe (shouldn't have dupes but just in case)
        const allLinks = [...linksAsA, ...linksAsB];
        const uniqueLinks = allLinks.filter((link, index, self) =>
          index === self.findIndex(l => l.id === link.id)
        );
        setLinks(uniqueLinks);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useAccountLinks] links-A listener error:', err.message);
        setLoading(false);
      }
    );

    const unsubscribeB = onSnapshot(
      queryB,
      (snapshot) => {
        linksAsB = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        // Merge and dedupe
        const allLinks = [...linksAsA, ...linksAsB];
        const uniqueLinks = allLinks.filter((link, index, self) =>
          index === self.findIndex(l => l.id === link.id)
        );
        setLinks(uniqueLinks);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useAccountLinks] links-B listener error:', err.message);
        setLoading(false);
      }
    );

    return () => {
      unsubscribeA();
      unsubscribeB();
    };
  }, [user?.uid]);

  // Categorize links
  const activeLinks = useMemo(() => {
    return links.filter((link) => link.status === 'active');
  }, [links]);

  const pendingReceived = useMemo(() => {
    return links.filter(
      (link) => link.status === 'pending' && link.initiatedBy !== user?.uid
    );
  }, [links, user?.uid]);

  const pendingSent = useMemo(() => {
    return links.filter(
      (link) => link.status === 'pending' && link.initiatedBy === user?.uid
    );
  }, [links, user?.uid]);

  // Get linked user info from a link (includes relationship context)
  const getLinkedUserInfo = useCallback(
    (link) => {
      if (!user?.uid) return null;
      const isUserA = link.userA === user.uid;
      const linkedUserId = isUserA ? link.userB : link.userA;
      const linkedUserDisplayName = isUserA ? link.userBDisplayName : link.userADisplayName;

      // Determine relationship role
      let role = 'peer';
      if (link.relationshipType === RELATIONSHIP_TYPES.MANAGER_REPORT && link.managerId) {
        if (link.managerId === user.uid) {
          role = 'report';  // They report to me
        } else if (link.managerId === linkedUserId) {
          role = 'manager'; // They are my manager
        }
      }

      return {
        userId: linkedUserId,
        email: isUserA ? link.userBEmail : link.userAEmail,
        displayName: linkedUserDisplayName,
        initials: (linkedUserDisplayName || '')
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .substring(0, 2),
        role,  // 'manager', 'report', or 'peer'
        relationshipType: link.relationshipType || RELATIONSHIP_TYPES.PEER
      };
    },
    [user?.uid]
  );

  // Get my manager from active links
  const manager = useMemo(() => {
    const managerLink = activeLinks.find(link => {
      if (link.relationshipType !== RELATIONSHIP_TYPES.MANAGER_REPORT) return false;
      // Manager is the other person if they are the managerId
      const isUserA = link.userA === user?.uid;
      const linkedUserId = isUserA ? link.userB : link.userA;
      return link.managerId === linkedUserId;
    });

    if (!managerLink) return null;
    return getLinkedUserInfo(managerLink);
  }, [activeLinks, user?.uid, getLinkedUserInfo]);

  // Get my direct reports (supervisors) from active links
  const directReports = useMemo(() => {
    return activeLinks
      .filter(link => {
        if (link.relationshipType !== RELATIONSHIP_TYPES.MANAGER_REPORT) return false;
        // I am the manager if managerId === my uid
        return link.managerId === user?.uid;
      })
      .map(link => ({
        ...getLinkedUserInfo(link),
        link
      }));
  }, [activeLinks, user?.uid, getLinkedUserInfo]);

  // Get sharing settings
  const getMySharingSettings = useCallback(
    (link) => {
      if (!user?.uid) return null;
      return link.userA === user.uid ? link.userASharing : link.userBSharing;
    },
    [user?.uid]
  );

  const getTheirSharingSettings = useCallback(
    (link) => {
      if (!user?.uid) return null;
      return link.userA === user.uid ? link.userBSharing : link.userASharing;
    },
    [user?.uid]
  );

  // Search for a user by email
  const searchUser = useCallback(
    async (email) => {
      if (!user?.uid || !email) return;

      const normalizedEmail = email.trim().toLowerCase();

      // Can't link to yourself
      if (normalizedEmail === user.email?.toLowerCase()) {
        setSearchError("You cannot link to yourself");
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      setSearchResult(null);

      try {
        // Search for user by email
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', normalizedEmail)
        );
        const snapshot = await getDocs(usersQuery);

        if (snapshot.empty) {
          setSearchError('No user found with that email');
          return;
        }

        const foundUser = {
          id: snapshot.docs[0].id,
          ...snapshot.docs[0].data()
        };

        // Check if link already exists
        const existingLink = links.find(
          (link) =>
            (link.userA === user.uid && link.userB === foundUser.id) ||
            (link.userB === user.uid && link.userA === foundUser.id)
        );

        if (existingLink) {
          setSearchError('You already have a link with this user');
          return;
        }

        setSearchResult(foundUser);
      } catch (error) {
        console.error('Error searching user:', error);
        setSearchError('Error searching for user');
      } finally {
        setIsSearching(false);
      }
    },
    [user?.uid, user?.email, links]
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchResult(null);
    setSearchError(null);
  }, []);

  // Send link request
  // relationshipType: 'manager-report' or 'peer' (default: 'peer')
  // managerId: required if relationshipType is 'manager-report' - the UID of who is the manager
  const sendLinkRequest = useCallback(
    async (targetUser, { relationshipType = RELATIONSHIP_TYPES.PEER, managerId = null } = {}) => {
      if (!user?.uid || !targetUser?.id) return;

      const linkData = {
        userA: user.uid,
        userB: targetUser.id,
        userAEmail: user.email,
        userBEmail: targetUser.email,
        userADisplayName: userProfile?.displayName || user.displayName || user.email,
        userBDisplayName: targetUser.displayName || targetUser.email,
        status: 'pending',
        initiatedBy: user.uid,
        participants: [user.uid, targetUser.id],
        relationshipType,
        userASharing: {
          goals: true,
          policies: true,
          oneOnOnes: true,
          meetings: true
        },
        userBSharing: {
          goals: true,
          policies: true,
          oneOnOnes: true,
          meetings: true
        },
        createdAt: serverTimestamp()
      };

      // Add managerId for manager-report relationships
      if (relationshipType === RELATIONSHIP_TYPES.MANAGER_REPORT && managerId) {
        linkData.managerId = managerId;
      }

      await addDoc(collection(db, 'accountLinks'), linkData);
      clearSearch();
    },
    [user?.uid, user?.email, user?.displayName, userProfile?.displayName, clearSearch]
  );

  // Accept link request
  const acceptLink = useCallback(async (linkId) => {
    await updateDoc(doc(db, 'accountLinks', linkId), {
      status: 'active',
      acceptedAt: serverTimestamp()
    });
  }, []);

  // Decline link request
  const declineLink = useCallback(async (linkId) => {
    await updateDoc(doc(db, 'accountLinks', linkId), {
      status: 'declined',
      declinedAt: serverTimestamp()
    });
  }, []);

  // Cancel pending request
  const cancelRequest = useCallback(async (linkId) => {
    await deleteDoc(doc(db, 'accountLinks', linkId));
  }, []);

  // Remove active link
  const removeLink = useCallback(async (linkId) => {
    await deleteDoc(doc(db, 'accountLinks', linkId));
  }, []);

  // Update sharing settings
  const updateMySharingSettings = useCallback(
    async (linkId, link, settings) => {
      if (!user?.uid) return;
      const field = link.userA === user.uid ? 'userASharing' : 'userBSharing';
      await updateDoc(doc(db, 'accountLinks', linkId), {
        [field]: settings
      });
    },
    [user?.uid]
  );

  // Toggle individual sharing settings
  const toggleGoalsSharing = useCallback(
    async (link) => {
      const settings = getMySharingSettings(link);
      if (!settings) return;
      await updateMySharingSettings(link.id, link, {
        ...settings,
        goals: !settings.goals
      });
    },
    [getMySharingSettings, updateMySharingSettings]
  );

  const togglePoliciesSharing = useCallback(
    async (link) => {
      const settings = getMySharingSettings(link);
      if (!settings) return;
      await updateMySharingSettings(link.id, link, {
        ...settings,
        policies: !settings.policies
      });
    },
    [getMySharingSettings, updateMySharingSettings]
  );

  const toggleOneOnOnesSharing = useCallback(
    async (link) => {
      const settings = getMySharingSettings(link);
      if (!settings) return;
      await updateMySharingSettings(link.id, link, {
        ...settings,
        oneOnOnes: !settings.oneOnOnes
      });
    },
    [getMySharingSettings, updateMySharingSettings]
  );

  const toggleMeetingsSharing = useCallback(
    async (link) => {
      const settings = getMySharingSettings(link);
      if (!settings) return;
      await updateMySharingSettings(link.id, link, {
        ...settings,
        meetings: !settings.meetings
      });
    },
    [getMySharingSettings, updateMySharingSettings]
  );

  // Summary text for sharing settings
  const getSharingSummary = useCallback((settings) => {
    if (!settings) return 'None';
    const shared = [];
    if (settings.goals) shared.push('Goals');
    if (settings.policies) shared.push('Policies');
    if (settings.oneOnOnes) shared.push('1:1s');
    if (settings.meetings) shared.push('Meetings');
    return shared.length > 0 ? shared.join(', ') : 'None';
  }, []);

  // Update relationship type on an existing link
  const updateRelationship = useCallback(
    async (linkId, relationshipType, managerId = null) => {
      const updates = { relationshipType };
      if (relationshipType === RELATIONSHIP_TYPES.MANAGER_REPORT && managerId) {
        updates.managerId = managerId;
      } else if (relationshipType === RELATIONSHIP_TYPES.PEER) {
        updates.managerId = null;
      }
      await updateDoc(doc(db, 'accountLinks', linkId), updates);
    },
    []
  );

  // Get relationship summary text
  const getRelationshipSummary = useCallback((link) => {
    if (!link || !user?.uid) return 'Linked';

    if (link.relationshipType === RELATIONSHIP_TYPES.MANAGER_REPORT && link.managerId) {
      if (link.managerId === user.uid) {
        return 'Reports to you';
      } else {
        return 'Your manager';
      }
    }
    return 'Peer';
  }, [user?.uid]);

  return {
    links,
    activeLinks,
    pendingReceived,
    pendingSent,
    loading,
    searchResult,
    searchError,
    isSearching,
    // Manager/report relationships
    manager,
    directReports,
    // Actions
    searchUser,
    clearSearch,
    sendLinkRequest,
    acceptLink,
    declineLink,
    cancelRequest,
    removeLink,
    updateRelationship,
    // Info helpers
    getLinkedUserInfo,
    getMySharingSettings,
    getTheirSharingSettings,
    getSharingSummary,
    getRelationshipSummary,
    // Sharing toggles
    toggleGoalsSharing,
    togglePoliciesSharing,
    toggleOneOnOnesSharing,
    toggleMeetingsSharing,
    pendingCount: pendingReceived.length
  };
}
